import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance } from '../../auth/msalConfig.js';

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

/**
 * Delegated Graph scopes for reading the signed-in member's own calendar plus their
 * Outlook category list (master categories live under mailbox settings). The events are
 * fetched entirely in the browser with the member's token — the PSA backend never sees
 * calendar data.
 */
const SCOPES = ['Calendars.Read', 'MailboxSettings.Read'];

/** Safety cap when walking @odata.nextLink pages. */
const MAX_PAGES = 5;

export interface CalendarWeekEvent {
  id: string;
  subject: string;
  /** Local wall-clock start, e.g. "2026-08-10T09:00:00.0000000" (no offset). */
  start: string;
  end: string;
  isAllDay: boolean;
  /** Outlook free/busy status: free | tentative | busy | oof | workingElsewhere. */
  showAs: string | null;
  organizer: string | null;
  location: string | null;
  /** Outlook category names assigned to the event. */
  categories: string[];
}

export type CalendarWeekResult =
  | { ok: true; events: CalendarWeekEvent[]; categories: string[] }
  | { ok: false; message: string };

interface RawGraphEvent {
  id?: string;
  subject?: string | null;
  isAllDay?: boolean;
  showAs?: string | null;
  categories?: string[];
  start?: { dateTime?: string };
  end?: { dateTime?: string };
  organizer?: { emailAddress?: { name?: string; address?: string } };
  location?: { displayName?: string };
}

/** yyyy-mm-dd for the day `days` after a yyyy-mm-dd date (UTC math, DST-immune). */
function addDaysIso(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  const date = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days));
  return date.toISOString().slice(0, 10);
}

/**
 * Fetches the user's Outlook master category list (the full set of categories defined
 * in O365, not just the ones used this week). Best-effort — returns [] on any failure
 * so the calendar view still works when mailbox settings can't be read.
 */
async function fetchMasterCategories(accessToken: string): Promise<string[]> {
  try {
    const res = await fetch(`${GRAPH_BASE}/me/outlook/masterCategories?$top=100`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as { value?: { displayName?: string | null }[] };
    return (json.value ?? [])
      .map((c) => c.displayName?.trim() ?? '')
      .filter((name) => name.length > 0);
  } catch {
    return [];
  }
}

/**
 * Fetches the signed-in member's Outlook calendar events for the week starting at
 * `weekStartIso` (7 days). Uses `/me/calendarView` with a `Prefer: outlook.timezone`
 * header so the window boundaries and returned times are all in the browser's timezone.
 * Falls back to an interactive popup when the scopes have not been consented yet.
 */
export async function fetchCalendarWeek(weekStartIso: string): Promise<CalendarWeekResult> {
  if (!msalInstance) {
    return { ok: false, message: 'Sign-in is not configured in this environment.' };
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return { ok: false, message: 'No signed-in Microsoft account was found.' };
  }

  let accessToken: string;
  try {
    accessToken = (await msalInstance.acquireTokenSilent({ scopes: SCOPES, account })).accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      try {
        accessToken = (await msalInstance.acquireTokenPopup({ scopes: SCOPES, account }))
          .accessToken;
      } catch {
        return {
          ok: false,
          message:
            'Permission to read your calendar was not granted (popup blocked or consent declined).',
        };
      }
    } else {
      return { ok: false, message: 'Could not get a Microsoft Graph token.' };
    }
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const weekEndIso = addDaysIso(weekStartIso, 7);
  const params = new URLSearchParams({
    startDateTime: `${weekStartIso}T00:00:00`,
    endDateTime: `${weekEndIso}T00:00:00`,
    $select: 'subject,start,end,isAllDay,showAs,organizer,location,categories',
    $orderby: 'start/dateTime',
    $top: '100',
  });

  const events: CalendarWeekEvent[] = [];
  let url: string | null = `${GRAPH_BASE}/me/calendarView?${params.toString()}`;
  for (let page = 0; page < MAX_PAGES && url; page += 1) {
    let res: Response;
    try {
      res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Prefer: `outlook.timezone="${timeZone}"`,
        },
      });
    } catch {
      return { ok: false, message: 'Microsoft Graph was unreachable.' };
    }
    if (!res.ok) {
      let detail = `HTTP ${res.status}`;
      try {
        const json = (await res.json()) as { error?: { message?: string } };
        detail = json.error?.message || detail;
      } catch {
        // Keep the status-code detail.
      }
      return { ok: false, message: `Microsoft Graph rejected the request: ${detail}` };
    }
    const body = (await res.json()) as { value?: RawGraphEvent[]; '@odata.nextLink'?: string };
    for (const raw of body.value ?? []) {
      if (!raw.start?.dateTime || !raw.end?.dateTime) continue;
      events.push({
        id: raw.id ?? `${raw.start.dateTime}|${raw.subject ?? ''}`,
        subject: raw.subject?.trim() || '(No subject)',
        start: raw.start.dateTime,
        end: raw.end.dateTime,
        isAllDay: raw.isAllDay === true,
        showAs: raw.showAs ?? null,
        organizer:
          raw.organizer?.emailAddress?.name || raw.organizer?.emailAddress?.address || null,
        location: raw.location?.displayName?.trim() || null,
        categories: (raw.categories ?? []).map((c) => c.trim()).filter((c) => c.length > 0),
      });
    }
    url = body['@odata.nextLink'] ?? null;
  }

  // Full O365 category list, unioned with any category seen on the week's events (an
  // event can keep a category that was later deleted from the master list).
  const categorySet = new Set(await fetchMasterCategories(accessToken));
  for (const event of events) {
    for (const category of event.categories) categorySet.add(category);
  }
  const categories = [...categorySet].sort((a, b) => a.localeCompare(b));

  return { ok: true, events, categories };
}
